import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const networkTopologyComponent: ComponentDef = {
  id: 'network-topology',
  name: 'Network Topology',
  description: 'Node connectivity shapes like ring, tree, and full-mesh affect collective bandwidth.',
  category: 'parallelism',
  params: [
    { name: 'n_gpus', label: 'GPUs', type: 'number', default: 8, min: 2, max: 1024, step: 1 },
    {
      name: 'variant',
      label: 'Topology',
      type: 'select',
      default: 'ring',
      options: [
        { label: 'Ring', value: 'ring' },
        { label: 'Tree', value: 'tree' },
        { label: 'Full Mesh', value: 'mesh' },
        { label: 'NCCL Algorithms', value: 'nccl_algorithms' }
      ]
    },
    {
      name: 'interconnect',
      label: 'Interconnect',
      type: 'select',
      default: 'nvswitch',
      options: [
        { label: 'NVSwitch', value: 'nvswitch' },
        { label: 'NVLink P2P', value: 'nvlink-p2p' },
        { label: 'PCIe', value: 'pcie' }
      ]
    }
  ],
  buildGraph: (params) => {
    const n_gpus = Number(params.n_gpus);
    const variant = String(params.variant);
    const interconnect = String(params.interconnect);

    const g = new GraphBuilder();
    g.input('data', [n_gpus]);

    if (variant === 'nccl_algorithms') {
      // Show how NCCL implements collective operations
      g.split('chunks', 'data', [n_gpus], 'Chunks');

      // Ring AllReduce: reduce-scatter + all-gather in ring
      g.reduceScatter('ring_rs', 'chunks', [n_gpus / n_gpus]);
      g.allgather('ring_ag', 'ring_rs', [n_gpus]);
      g.metric('ring_ar', 'ring_ag', 'Ring AllReduce: 2(N-1)/N bw');

      // Tree AllReduce: log-N reduction tree
      g.allreduce('tree_reduce', 'chunks', [n_gpus]);
      g.metric('tree_ar', 'tree_reduce', 'Tree AllReduce: log(N) steps');

      // Recursive halving-doubling
      g.split('hd_chunks', 'data', [n_gpus], 'HD pairs');
      g.all2all('hd_exchange', 'hd_chunks', [n_gpus]);
      g.metric('hd_ar', 'hd_exchange', 'Recursive Halving-Doubling');

      g.output('out_ring', 'ring_ar');
      g.output('out_tree', 'tree_ar');
      g.output('out_hd', 'hd_ar');
      return g.build();
    }

    g.split('nodes', 'data', [n_gpus], 'GPUs');

    // Topology determines communication pattern
    if (variant === 'ring') {
      g.p2p('ring_hop1', 'nodes', [n_gpus]);
      g.allreduce('ring', 'ring_hop1', [n_gpus]);
    } else if (variant === 'tree') {
      g.allreduce('tree_reduce', 'nodes', [n_gpus]);
      g.replicate('tree_bcast', 'tree_reduce', [n_gpus], 2);
      g.add('ring', 'tree_reduce', 'tree_bcast');
    } else {
      // mesh: all-to-all connectivity
      g.all2all('mesh_a2a', 'nodes', [n_gpus]);
      g.allreduce('ring', 'mesh_a2a', [n_gpus]);
    }

    // Interconnect determines bandwidth characteristics
    g.scale('topology', 'ring', String(variant));
    if (interconnect === 'nvswitch') {
      g.metric('bw', 'topology', 'NVSwitch: 900 GB/s bidirectional');
    } else if (interconnect === 'nvlink-p2p') {
      g.metric('bw', 'topology', `NVLink: 600 GB/s (${Math.min(n_gpus, 8)}-GPU direct)`);
    } else {
      g.metric('bw', 'topology', 'PCIe: 64 GB/s (shared bus)');
    }

    // Show latency impact
    if (interconnect === 'pcie') {
      g.metric('latency', 'bw', 'High latency: CPU bottleneck');
    } else if (interconnect === 'nvlink-p2p') {
      g.metric('latency', 'bw', 'Medium latency: hop-dependent');
    } else {
      g.metric('latency', 'bw', 'Low latency: full-bisection');
    }

    g.output('out', 'latency');
    return g.build();
  }
};
